import { Configs } from "../../logic/Configs";
import { Cookies } from "../../util/Cookies";
import { Token } from "../../util/Token";
import { Ajax } from "./Ajax";

/**
 * The fetch access to the server methods: GET, POST and DELETE
 * with different return types and authentication / error events.
 */
export class AjaxFetch implements Ajax {
  /**
   * By default (true) the token is set to salaxy-token -cookie.
   * Disable cookies with this flag.
   */
  public useCookie = true;

  /**
   * By default credentials are not used in http-calls.
   * Enable credentials with this flag (force disabled when the token is set).
   */
  public useCredentials: boolean;

  /**
   * The server address - root of the server. This is settable field.
   * Will probably be changed to a configuration object in the final version.
   */
  public serverAddress = "https://test-api.salaxy.com";

  private token: string | null;

  /**
   * Creates a new instance of AjaxFetch
   */
  constructor() {
    const config = Configs.current;
    if (config) {
      // apiServer
      if (config.apiServer) {
        this.serverAddress = config.apiServer;
      }

      // useCredentials
      if (config.useCredentials != null) {
        this.useCredentials = config.useCredentials;
      }

      // useCookie
      if (config.useCookie != null) {
        this.useCookie = config.useCookie;
      }
    }
  }

  /**
   * Gets the API address with version information. E.g. 'https://test-api.salaxy.com/v03/api'.
   * Default version, as of writing 2.0
   */
  public getApiAddress(): string {
    return this.serverAddress + "/v03/api";
  }

  /** Gets the Server address that is used as bases to the HTML queries. E.g. 'https://test-api.salaxy.com' */
  public getServerAddress(): string {
    return this.serverAddress;
  }

  /**
   * Gets the current token.
   * Will check the salaxy-token cookie if the token is persisted there
   */
  public getCurrentToken(): string | null {
    if (!this.token && this.useCookie) {
      this.token = new Cookies().get("salaxy-token") || "";
    }
    return this.token;
  }

  /** Gets the status of the current token. */
  public getTokenStatus(): "noToken" | "ok" | "expired" | "invalid" {
    const token = this.getCurrentToken();
    return Token.validate(token);
  }

  /**
   * Sets the current token. Persists it to cookie until the browser window
   *
   * @param token - the authentication token to persist.
   */
  public setCurrentToken(token: string | null): void {
    if (this.useCookie) {
      new Cookies().setCookie("salaxy-token", token || "");
    }
    this.token = token;
  }

  /**
   * POSTS data to server and receives back a JSON-message.
   *
   * @param method - The API method is the url path after the api version segments (e.g. '/v03/api')
   * and starts with a forward slash, e.g. '/calculator/new'.
   * @param data - The data that is posted to the server.
   *
   * @returns A Promise with result data. Standard Promise rejection to be used for error handling.
   */
  public async postJSON(method: string, data: any): Promise<any> {
    const init: RequestInit = this.setAuthorization({
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json, text/plain, */*; charset=UTF-8",
        "Content-Type": "application/json;charset=UTF-8",
      },
    });

    const response = await fetch(this.getUrl(method), init);
    if (!response.ok) {
      return this.handleErrors(response);
    }
    return await response.json();
  }

  /**
   * Gets a JSON-message from server using the API
   *
   * @param method The API method is the url path after the api version segments (e.g. '/v03/api')
   * and starts with a forward slash, e.g. '/calculator/new'.
   *
   * @returns A Promise with result data. Standard Promise rejection to be used for error handling.
   */
  public async getJSON(method: string): Promise<any> {
    const init: RequestInit = this.setAuthorization({
      method: "GET",
      headers: {
        Accept: "application/json, text/plain, */*; charset=UTF-8",
        "Content-Type": "application/json; charset=UTF-8",
      },
    });
    const response = await fetch(this.getUrl(method), init);
    if (!response.ok) {
      return this.handleErrors(response);
    }
    return await response.json();
  }

  /**
   * POSTS data to server and receives back a blob (binary data).
   *
   * @param method - The API method is the url path after the api version segments (e.g. '/v03/api')
   * and starts with a forward slash, e.g. '/calculator/new', or a full URL address.
   * @param data - The data that is posted to the server.
   * @returns A Promise that resolves to an object containing:
   * - data: The blob data from the response
   * - headers: An object containing the response headers as key-value pairs
   */
   public async postBlob(method: string, data: any): Promise<{data: Blob, headers: {[key: string]: string}}> {
    const init: RequestInit = this.setAuthorization({
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        Accept: "*/*",
        "Content-Type": "application/json; charset=UTF-8"
      },
    });

    const response = await fetch(this.getUrl(method), init);
    if (!response.ok) {
      return this.handleErrors(response);
    }
    const headers: {[key: string]: string} = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });
    return {data: await response.blob(), headers};
  }

  /**
   * Gets a Blob from server using the API
   *
   * @param method The API method is the url path after the api version segments (e.g. '/v03/api')
   * and starts with a forward slash, e.g. '/calculator/new'.
   *
   * @returns A Promise with result blob. Standard Promise rejection to be used for error handling.
   */
  public async getBlob(method: string): Promise<{data: Blob, headers: {[key: string]: string}}> {
    const init: RequestInit = this.setAuthorization({
      method: "GET",
      headers: {
        Accept: "*/*"
      },
    });
    const response = await fetch(this.getUrl(method), init);
    if (!response.ok) {
      return this.handleErrors(response);
    }
    const headers: {[key: string]: string} = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });
    return {data: await response.blob(), headers};
  }

  /**
   * POSTS data to server and receives back HTML.
   *
   * @param method - The API method is the url path after the api version segments (e.g. '/v03/api')
   * and starts with a forward slash, e.g. '/calculator/new'.
   * @param data - The data that is posted to the server.
   *
   * @returns A Promise with result data. Standard Promise rejection to be used for error handling.
   */
  public async postHTML(method: string, data: any): Promise<string> {
    const init: RequestInit = this.setAuthorization({
      method: "POST",
      body: (!data || typeof data === "string") ? data : JSON.stringify(data),
      headers: {
        Accept: "text",
        "Content-Type": "text/plain",
      },
    });

    const response = await fetch(this.getUrl(method), init);
    if (!response.ok) {
      return this.handleErrors(response);
    }
    return await response.text();
  }

  /**
   * Gets a HTML-message from server using the API
   *
   * @param method - The API method is the url path after the api version segments (e.g. '/v03/api')
   * and starts with a forward slash, e.g. '/calculator/new'.
   *
   * @returns A Promise with result data. Standard Promise rejection to be used for error handling.
   */
  public async getHTML(method: string): Promise<string> {
    const init: RequestInit = this.setAuthorization({
      method: "GET",
      headers: {
        Accept: "text"
      },
    });

    const response = await fetch(this.getUrl(method), init);
    if (!response.ok) {
      return this.handleErrors(response);
    }
    return await response.text();
  }

  /**
   * Sends a DELETE-message to server using the API
   *
   * @param method - The API method is the url path after the api version segments (e.g. '/v03/api')
   * and starts with a forward slash, e.g. '/calculator/new'.
   *
   * @returns A Promise with result data. Standard Promise rejection to be used for error handling.
   */
  public async remove(method: string): Promise<any> {
    const init: RequestInit = this.setAuthorization({
      method: "DELETE",
      headers: {
        Accept: "application/json; charset=UTF-8"
      },
    });

    const response = await fetch(this.getUrl(method), init);
    if (!response.ok) {
      return this.handleErrors(response);
    }
    return await response.json();
  }

  /** If missing, append the API server address to the given url method string */
  private getUrl(method: string): string {
    if (!method || method.trim() === "") {
      return "";
    }
    if (method.toLowerCase().startsWith("http")) {
      return method;
    }
    if (method.toLowerCase().startsWith("/v")) {
      return this.getServerAddress() + method;
    }
    return this.getApiAddress() + method;
  }

  private setAuthorization(options: RequestInit): RequestInit {
    const token = this.getCurrentToken();
    if (token) {
      (options.headers as any).Authorization = "Bearer " + token;
    } else if (this.useCredentials) {
      options.credentials = "include";
    }
    return options;
  }

  private handleErrors(response: Response): Promise<any> {
    (response as any).data = {
      error: `HTTP error: ${response.statusText} (${response.status})`,
      messageHtml: "",
    };
    return response.json().then((content) => {
      if (content) {
        (response as any).data = content;
      }
      return Promise.reject(response);
    });
  }
}
